function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import * as React from 'react';
import { Button, Typography } from 'onboarding-components';
import moment from 'moment';
import copy from 'clipboard-copy';
import produce from 'immer';
import take from 'lodash/take';
import { addToast } from 'common/components/toast-area/toast-manager';
import { ToastType } from 'common/components/toast/types';
import { TEXT_HIGHLIGHT_THEME, TextHighlightReact } from 'common/components/text-highlight';
import { IMMERSIVE_ASSETS } from 'common/onboarding/immersive-onboarding-legacy/assets';
import { Content, Header } from 'common/onboarding/immersive-onboarding-legacy/components';
import { reporter } from 'common/report/error-reporting';
import { appApiUrl, handleJsonRequest } from 'common/report/queries/utils';
import { apiClient } from 'common/util/api-client';
import { Banner } from 'common/onboarding/immersive-onboarding-legacy/components/banner';
import { CheckIntegrationResult, SetupGuideContext, Step } from 'common/onboarding/immersive-onboarding-legacy/types';
import { useMountEffect } from 'common/onboarding/utils';
import { SetUpTrackingEvent, track, TrackingEvent, TrackingProperty, trackSetUp } from 'common/onboarding/immersive-onboarding-legacy/tracking';
import { ApiClient } from 'common/onboarding/immersive-onboarding-legacy/api-client';
import { TooltipReact } from 'common/components/tooltip';
import { Placement, TooltipTextAlign } from 'common/components/tooltip/types';
import { useCurrentUser } from 'common/contexts/react/current-user-context';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { getLocation } from 'common/util/url';
import { Trans } from 'common/i18n';
import { useTranslation } from 'common/i18n/hooks';
import textHighlightStyleOverride from './textHighlight.inline.styl';
import styles from './implement-phase2.module.styl';
import { DuplicateIcon } from './duplicate-icon';
import { EventListener } from './event-listener';
type ImplementMethodProps = {
  title: string;
  url: string;
  imgSrc: string;
  highlight?: string;
};
const MAX_OWNER_DISPLAY_COUNT = 4;
const ImplementMethod = (props: ImplementMethodProps) => {
  return <a className={styles.implementMethod} href={props.url} target='_blank' onClick={() => {
    trackSetUp(SetUpTrackingEvent.LinkClicked, {
      cta: props.title,
      [TrackingProperty.Phase2]: true
    });
  }} data-sentry-component="ImplementMethod" data-sentry-source-file="implement-phase2.tsx">
      <div className={styles.implementMethodIcon}>
        <img src={props.imgSrc} />
      </div>
      <div className={styles.implementMethodTextContainer}>
        <Typography variant='captionXs' weight='bold' className={styles.implementMethodText} data-sentry-element="Typography" data-sentry-source-file="implement-phase2.tsx">
          {props.title}
        </Typography>
        {props.highlight && <TextHighlightReact style-override={textHighlightStyleOverride} size='small' theme={TEXT_HIGHLIGHT_THEME.SUCCESS} text={props.highlight} />}
      </div>
    </a>;
};
export const Implement = () => {
  const {
    formatList,
    t
  } = useTranslation();
  const {
    completed,
    isIntegrated,
    setIsIntegrated,
    setupGuideInfo,
    setSetupGuideInfo,
    setStep
  } = React.useContext(SetupGuideContext);
  const currentUser = useCurrentUser();
  const currentProject = useCurrentProject();
  const [isCopying, setIsCopying] = React.useState<boolean>(false);
  const [owners, setOwners] = React.useState([]);
  const pollingRef = React.useRef(null);
  const paths = getLocation().pathname.split("/");
  const projectId = paths[paths.indexOf("project") + 1];
  const getProjectIntegrationStatus = async () => {
    try {
      const res: CheckIntegrationResult = await apiClient.get(appApiUrl("check-integration", {
        projectId
      }));
      if (res.is_integrated) {
        if (setupGuideInfo) {
          const updatedStepsCompleted = produce(setupGuideInfo.stepsCompleted, draft => draft.add(Step.Implement));
          ApiClient.updateCompleted(setupGuideInfo.projectId, updatedStepsCompleted);
          setSetupGuideInfo(_objectSpread(_objectSpread({}, setupGuideInfo), {}, {
            stepsCompleted: updatedStepsCompleted
          }));
        }
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      setIsIntegrated(res.is_integrated);
      trackSetUp(SetUpTrackingEvent.IntegratedWithinSession, {
        [TrackingProperty.Phase2]: true
      });
    } catch (error) {
      reporter.error(error);
    }
  };
  const getOwnersAccount = async () => {
    try {
      var _currentProject$get;
      const {
        results
      } = await handleJsonRequest(fetch("/organizations/".concat((_currentProject$get = currentProject.get()) === null || _currentProject$get === void 0 ? void 0 : _currentProject$get.value.organizationId, "/users"), {
        method: "GET"
      }));
      setOwners(results.filter(user => user.org_role === "owner" || user.org_role === "admin").sort((a, b) => b.last_activity - a.last_activity));
    } catch (_unused) {
      throw new Error("Error retrieving users");
    }
  };
  const previouslyIntegrated = completed[Step.Implement] || isIntegrated;
  useMountEffect(() => {
    if (pollingRef.current === null && !previouslyIntegrated) {
      pollingRef.current = window.setInterval(getProjectIntegrationStatus, 10000);
    }
    getOwnersAccount();
    return () => {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    };
  });
  const copyToken = async (token: string) => {
    try {
      await copy(token);
      setIsCopying(true);
      setTimeout(() => {
        setIsCopying(false);
      }, 1500);
      trackSetUp(SetUpTrackingEvent.TokenCopied, {
        [TrackingProperty.Phase2]: true
      });
    } catch (err) {
      reporter.error(err);
      addToast({
        type: ToastType.ERROR,
        message: t("onboarding.implement.copy-token-error")
      });
    }
  };
  const getOwnersString = () => {
    let ownersEmail = take(owners, MAX_OWNER_DISPLAY_COUNT).reduce((acc, user) => acc += "".concat(user.email, "\n"), "");
    const numRemainingOwners = owners.slice(MAX_OWNER_DISPLAY_COUNT).length;
    ownersEmail += numRemainingOwners > 0 ? t("onboarding.implement.remaining-owners", {
      remainingOwners: owners.slice(MAX_OWNER_DISPLAY_COUNT).length
    }) : "";
    return ownersEmail;
  };
  const getOrgName = () => {
    var _currentProject$get2, _currentUser$get;
    const ordId = (_currentProject$get2 = currentProject.get()) === null || _currentProject$get2 === void 0 ? void 0 : _currentProject$get2.value.organizationId;
    return (_currentUser$get = currentUser.get()) === null || _currentUser$get === void 0 || (_currentUser$get = _currentUser$get.value) === null || _currentUser$get === void 0 || (_currentUser$get = _currentUser$get.organizations[ordId]) === null || _currentUser$get === void 0 ? void 0 : _currentUser$get.name;
  };
  const ProjectToken = () => {
    let tokenText: string;
    if (isCopying) {
      tokenText = t("onboarding.implement.copied");
    } else if (setupGuideInfo !== null && setupGuideInfo !== void 0 && setupGuideInfo.projectToken) {
      tokenText = setupGuideInfo.projectToken;
    }
    return <div className={styles.tokenContainer} data-sentry-component="ProjectToken" data-sentry-source-file="implement-phase2.tsx">
        <div className={styles.tokenHeader}>
          <Typography variant='captionXs' data-sentry-element="Typography" data-sentry-source-file="implement-phase2.tsx">{t("onboarding-legacy.implement.copy-token")}</Typography>
          <div className={styles.infoTooltip}>
            <img src={IMMERSIVE_ASSETS.help} className={styles.infoIcon} />
            <TooltipReact placement={Placement.Top} text={t("onboarding-legacy.implement.token-tooltip")} maxWidth={"200px"} data-sentry-element="TooltipReact" data-sentry-source-file="implement-phase2.tsx" />
          </div>
        </div>
        <button className={styles.copyTokenButton} onClick={() => !isCopying && copyToken("".concat(setupGuideInfo.projectToken))} disabled={!tokenText}>
          {/* hide the full token in code to prevent unauth access. text-overflow will ellipsify */}
          <div className={styles.tokenText}>{tokenText ? tokenText.slice(0, 12) : "*********"}</div>
          <DuplicateIcon className={styles.duplicateIcon} data-sentry-element="DuplicateIcon" data-sentry-source-file="implement-phase2.tsx" />
          {!tokenText && <TooltipReact placement={Placement.Bottom} text={t("onboarding-legacy.implement.no-permission-tooltip", {
          orgName: getOrgName(),
          ownersString: getOwnersString()
        })} maxWidth={"250px"} textAlign={TooltipTextAlign.Start} />}
        </button>
      </div>;
  };
  const eventList = events => {
    if (!events || events.length === 0) {
      return "";
    }
    if (events.length === 1) {
      return events[0];
    }
    return formatList(events);
  };
  const EventBanner = () => {
    var _currentUser$get$valu, _Object$keys;
    if (!(setupGuideInfo !== null && setupGuideInfo !== void 0 && setupGuideInfo.eventsPropertiesMap)) {
      return null;
    }
    const lastEditedUser = ((_currentUser$get$valu = currentUser.get().value) === null || _currentUser$get$valu === void 0 ? void 0 : _currentUser$get$valu.email) === (setupGuideInfo === null || setupGuideInfo === void 0 ? void 0 : setupGuideInfo.email) ? t("onboarding.implement.you") : setupGuideInfo === null || setupGuideInfo === void 0 ? void 0 : setupGuideInfo.author;
    const lastEditedDate = setupGuideInfo !== null && setupGuideInfo !== void 0 && setupGuideInfo.modified ? moment(setupGuideInfo.modified).format("MMM D, YYYY") : "";
    const plannedEvents = (_Object$keys = Object.keys(setupGuideInfo === null || setupGuideInfo === void 0 ? void 0 : setupGuideInfo.eventsPropertiesMap)) !== null && _Object$keys !== void 0 ? _Object$keys : [];
    return <Banner isVisible={plannedEvents.length > 0} data-sentry-element="Banner" data-sentry-component="EventBanner" data-sentry-source-file="implement-phase2.tsx">
        <Banner.Content title={t("onboarding-legacy.implement.planned-events", {
        eventList: eventList(plannedEvents)
      })} description={t("onboarding-legacy.implement.last-edited", {
        lastEditedUser,
        lastEditedDate
      })} data-sentry-element="unknown" data-sentry-source-file="implement-phase2.tsx" />
        <Banner.Action action={() => {
        setStep(Step.Events);
        track(TrackingEvent.EditEvent, {
          [TrackingProperty.Phase2]: true
        });
      }} actionName={t("onboarding-legacy.implement.view-planned-events")} data-sentry-element="unknown" data-sentry-source-file="implement-phase2.tsx" />
      </Banner>;
  };
  const ImplementCdpCTA = () => {
    return <div className={styles.implementSection} data-sentry-component="ImplementCdpCTA" data-sentry-source-file="implement-phase2.tsx">
        <Typography variant='bodySm' weight='bold' data-sentry-element="Typography" data-sentry-source-file="implement-phase2.tsx">
          {t("onboarding-legacy.implement.implemented-cdp")}
        </Typography>
        <div className={styles.implementMethodsGrid}>
          <ImplementMethod title={"Segment"} url={"https://developer.mixpanel.com/docs/segment"} imgSrc={IMMERSIVE_ASSETS.segment} highlight={t("onboarding-legacy.implement.popular")} data-sentry-element="ImplementMethod" data-sentry-source-file="implement-phase2.tsx" />
          <ImplementMethod title={"BigQuery"} url={"https://docs.mixpanel.com/docs/tracking/data-warehouse?dwh=bigquery"} imgSrc={IMMERSIVE_ASSETS.bq} data-sentry-element="ImplementMethod" data-sentry-source-file="implement-phase2.tsx" />
          <ImplementMethod title={"Snowflake"} url={"https://docs.mixpanel.com/docs/tracking-methods/data-warehouse?dwh=snowflake"} imgSrc={IMMERSIVE_ASSETS.snowflake} data-sentry-element="ImplementMethod" data-sentry-source-file="implement-phase2.tsx" />
          <ImplementMethod title={"RudderStack"} url={"https://developer.mixpanel.com/docs/rudderstack"} imgSrc={IMMERSIVE_ASSETS.rudderstack} data-sentry-element="ImplementMethod" data-sentry-source-file="implement-phase2.tsx" />
          <ImplementMethod title={"Redshift"} url={"https://docs.mixpanel.com/docs/tracking-methods/data-warehouse?dwh=redshift"} imgSrc={IMMERSIVE_ASSETS.redshift} data-sentry-element="ImplementMethod" data-sentry-source-file="implement-phase2.tsx" />
          <ImplementMethod title={t("onboarding-legacy.implement.more-options")} url={"https://docs.mixpanel.com/docs/tracking-methods/choosing-the-right-method"} imgSrc={IMMERSIVE_ASSETS.more} data-sentry-element="ImplementMethod" data-sentry-source-file="implement-phase2.tsx" />
        </div>
      </div>;
  };
  const ImplementSdkCTA = () => {
    return <div className={styles.implementSection} data-sentry-component="ImplementSdkCTA" data-sentry-source-file="implement-phase2.tsx">
        <Typography variant='bodySm' weight='bold' data-sentry-element="Typography" data-sentry-source-file="implement-phase2.tsx">
          {t("onboarding-legacy.implement.first-analytics-tool")}
        </Typography>
        <Typography variant='captionXs' data-sentry-element="Typography" data-sentry-source-file="implement-phase2.tsx">
          <Trans i18nKey={"onboarding.implement.sdk-includes-autocapture"} t={t} components={{
          docLink: <a href='https://docs.mixpanel.com/docs/tracking-methods/autocapture' target='_blank' />
        }} data-sentry-element="Trans" data-sentry-source-file="implement-phase2.tsx" />
        </Typography>
        <div className={styles.implementMethodsGrid}>
          <ImplementMethod title={"JavaScript"} url={"https://developer.mixpanel.com/docs/javascript-quickstart"} imgSrc={IMMERSIVE_ASSETS.js} highlight={t("onboarding-legacy.implement.popular")} data-sentry-element="ImplementMethod" data-sentry-source-file="implement-phase2.tsx" />
          <ImplementMethod title={"Android"} url={"https://developer.mixpanel.com/docs/android-quickstart"} imgSrc={IMMERSIVE_ASSETS.android} highlight={t("onboarding-legacy.implement.popular")} data-sentry-element="ImplementMethod" data-sentry-source-file="implement-phase2.tsx" />
          <ImplementMethod title={"iOS"} url={"https://developer.mixpanel.com/docs/ios-swift-quickstart"} imgSrc={IMMERSIVE_ASSETS.ios} highlight={t("onboarding-legacy.implement.popular")} data-sentry-element="ImplementMethod" data-sentry-source-file="implement-phase2.tsx" />
          <ImplementMethod title={"React Native"} url={"https://developer.mixpanel.com/docs/react-native-quickstart"} imgSrc={IMMERSIVE_ASSETS.reactNative} data-sentry-element="ImplementMethod" data-sentry-source-file="implement-phase2.tsx" />
          <ImplementMethod title={"Google Tag Manager"} url={"https://docs.mixpanel.com/docs/tracking/integrations/google-tag-manager"} imgSrc={IMMERSIVE_ASSETS.gtm} data-sentry-element="ImplementMethod" data-sentry-source-file="implement-phase2.tsx" />
          <ImplementMethod title={t("onboarding-legacy.implement.more-options")} url={"https://docs.mixpanel.com/docs/tracking-methods/choosing-the-right-method"} imgSrc={IMMERSIVE_ASSETS.more} data-sentry-element="ImplementMethod" data-sentry-source-file="implement-phase2.tsx" />
        </div>
      </div>;
  };
  const SectionButton = () => {
    const {
      modalManager
    } = React.useContext(SetupGuideContext);
    return <div className={styles.implementFooterButton} data-sentry-component="SectionButton" data-sentry-source-file="implement-phase2.tsx">
        <Button disabled={!previouslyIntegrated} onClick={() => {
        modalManager.closeModal();
        track(TrackingEvent.CloseModal, {
          [TrackingProperty.Phase2]: true
        });
      }} data-sentry-element="Button" data-sentry-source-file="implement-phase2.tsx">
          {t("onboarding-legacy.implement.finish-and-close")}
        </Button>
      </div>;
  };
  return <>
      <Header title={t("onboarding-legacy.implement.connect-data-header")} step={t("onboarding-legacy.implement.step-three")} duration={15} imgSrc={IMMERSIVE_ASSETS.sendData} completed={completed[Step.Implement]} data-sentry-element="Header" data-sentry-source-file="implement-phase2.tsx" />
      <Content data-sentry-element="Content" data-sentry-source-file="implement-phase2.tsx">
        <div className={styles.implementHeader}>
          <div>
            <Typography className={styles.headerText} variant='headingSm' data-sentry-element="Typography" data-sentry-source-file="implement-phase2.tsx">
              {t("onboarding-legacy.implement.choose-connection-method")}
            </Typography>
            <Typography className={styles.subheaderText} variant='bodySm' data-sentry-element="Typography" data-sentry-source-file="implement-phase2.tsx">
              <Trans i18nKey={"onboarding.implement.implementation-guide"} t={t} components={{
              guideLink: <a href='https://docs.mixpanel.com/docs/tracking-methods/choosing-the-right-method' target='_blank' />
            }} data-sentry-element="Trans" data-sentry-source-file="implement-phase2.tsx" />
            </Typography>
          </div>
          <ProjectToken data-sentry-element="ProjectToken" data-sentry-source-file="implement-phase2.tsx" />
        </div>
        <ImplementCdpCTA data-sentry-element="ImplementCdpCTA" data-sentry-source-file="implement-phase2.tsx" />
        <ImplementSdkCTA data-sentry-element="ImplementSdkCTA" data-sentry-source-file="implement-phase2.tsx" />
        <EventListener isIntegrated={previouslyIntegrated} data-sentry-element="EventListener" data-sentry-source-file="implement-phase2.tsx" />
        <EventBanner data-sentry-element="EventBanner" data-sentry-source-file="implement-phase2.tsx" />
        <SectionButton data-sentry-element="SectionButton" data-sentry-source-file="implement-phase2.tsx" />
      </Content>
    </>;
};